import { FormattedMessage, FormattedNumber } from 'react-intl';
import { getSellerReviewsUrl, getSellerUrl } from '@/utils/urls';
import React, { Component } from 'react';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import classNames from 'classnames';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import PageTitle from '@/components/PageTitle/PageTitle';
import RatingsPageBreadcrumbs from './RatingsPageBreadcrumbs';
import RatingsPageHeaderMeta from './RatingsPageHeaderMeta';
import RatingsSummary from './RatingsSummary';
import Reviews from './Reviews';
import scrollIntoView from 'scroll-into-view';
import styles from './RatingsPage.module.scss';

type Props = {
    baseUrl: string;
    dismissReviewsModal?: Function;
    fetchBidderStatusesByIdIfNeeded: Function;
    fetchHelpfulCountsByIdIfNeeded: Function;
    fullPage: boolean;
    helpfulCounts: any;
    loadSellerRatingsById: Function;
    mobile: boolean;
    modal?: boolean;
    openReportReviewModal: Function;
    percentiles: any;
    queryParams?: any;
    ratings: any;
    replace: Function;
    reviewComments: any;
    seller: any;
    sellerId: number;
    setBidderReviewStatus: Function;
    statuses: any;
    submitted?: boolean;
    whiteLabel?: boolean;
};

type State = {
    filter: string;
    page: number;
    pageSize: number;
    sort: string;
};

export default class RatingsPage extends Component<Props, State> {
    static defaultProps = {
        dismissReviewsModal: () => null,
    };

    state = {
        filter: !this.props.fullPage ? 'hasreview' : this.props.queryParams?.filter || '',
        page: this.props.queryParams?.page || 1,
        pageSize: this.props.fullPage ? this.props.queryParams?.pageSize || 50 : 8,
        sort: this.props.queryParams?.sort || '-overall',
    };

    componentDidMount() {
        const { filter, page, pageSize, sort } = this.state;
        const sellerId = this.props.sellerId;
        this.props.loadSellerRatingsById({ filter, page, pageSize, sellerId, sort });
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { ratings, statuses } = this.props;
        const { filter, page, pageSize, sort } = this.state;
        if (this.shouldRedirect()) {
            this.redirectToSellerPage();
        }
        const sellerId = this.props.sellerId;
        if (
            prevState.filter !== filter ||
            prevState.page !== page ||
            prevState.pageSize !== pageSize ||
            prevState.sort !== sort
        ) {
            this.props.loadSellerRatingsById({ filter, page, pageSize, sellerId, sort });
        }
        const statusCount = Object.keys(statuses).length;
        if (Boolean(ratings?.reviews?.length)) {
            if (!statusCount || !isEqual(prevProps.ratings, ratings)) {
                const ids = ratings.reviews.map((r) => r.reviewId);
                this.props.fetchBidderStatusesByIdIfNeeded(ids);
                this.props.fetchHelpfulCountsByIdIfNeeded(ids);
            }
        }
    }

    shouldRedirect = () => {
        const { fullPage, ratings } = this.props;
        const notAllowed = Boolean(ratings?.houseId) && !ratings?.allowPublicReviews;

        return fullPage && notAllowed;
    };

    redirectToSellerPage = () => {
        const { seller } = this.props;
        const { name, sellerId } = seller;
        const sellerUrl = getSellerUrl(sellerId, name);
        if (Boolean(window)) {
            // @ts-ignore
            window.location = sellerUrl;
        }
    };

    scrollToReviews = () => {
        const reviewsSection = document.getElementById('reviews');
        scrollIntoView(reviewsSection, {
            align: {
                top: 0,
            },
        });
    };

    updateFilter = ({ value }: any) => this.setState({ filter: value, page: 1 });

    updateFilterFromBarChart = (filter: string) => {
        if (this.props.fullPage) {
            this.setState({ filter, page: 1 });
            this.scrollToReviews();
        } else {
            const { seller } = this.props;
            const { name, sellerId } = seller;
            const reviewsUrl = getSellerReviewsUrl(sellerId, name, filter);
            this.props.replace({
                pathname: reviewsUrl,
            });
        }
    };

    updateSort = ({ value }: any) => this.setState({ page: 1, sort: value });

    onChange = (pageInfo: any) => {
        this.setState(pageInfo);
        this.scrollToReviews();
    };

    render() {
        const {
            baseUrl,
            dismissReviewsModal,
            fullPage,
            helpfulCounts,
            mobile,
            modal,
            openReportReviewModal,
            percentiles,
            ratings,
            reviewComments,
            seller,
            setBidderReviewStatus,
            statuses,
            submitted,
            whiteLabel,
        } = this.props;

        const { filter, page, pageSize, sort } = this.state;

        const { name, sellerId } = seller;

        const countStyles = ratings.totalReviews > 0 ? styles.blueText : '';
        const totalReviews = <FormattedNumber value={ratings?.totalReviews || 0} />;
        const fullPageLink = getSellerReviewsUrl(sellerId, name);
        const canonicalURL = fullPageLink.split('?')[0];

        const reviewsCountLink = fullPage ? (
            <Link className={countStyles} onClick={this.scrollToReviews} route>
                ({totalReviews})
            </Link>
        ) : (
            <Link className={countStyles} route to={fullPageLink}>
                ({totalReviews})
            </Link>
        );

        const pageHeader = (
            <>
                <FormattedMessage id="review.bidder_reviews_name" values={{ name }} /> {reviewsCountLink}
            </>
        );

        const header = fullPage ? (
            <div className={styles.reviewHeader}>
                <PageTitle title={pageHeader}></PageTitle>
            </div>
        ) : (
            <div className={styles.reviewHeader}>
                <h2 className={styles.sellerPageHeader}>{pageHeader}</h2>
            </div>
        );

        const reviews = (
            <Reviews
                dismissReviewsModal={dismissReviewsModal}
                filter={filter}
                filteredCount={ratings.filteredReviewCount}
                fullPage={fullPage}
                fullPageLink={fullPageLink}
                helpfulCounts={helpfulCounts}
                mobile={mobile}
                onChange={this.onChange}
                openReportReviewModal={openReportReviewModal}
                page={page}
                pageSize={pageSize}
                reviewComments={reviewComments}
                reviews={ratings.reviews}
                seller={seller}
                setBidderReviewStatus={setBidderReviewStatus}
                sort={sort}
                statuses={statuses}
                submitted={submitted}
                totalReviews={ratings.totalReviews}
                updateFilter={this.updateFilter}
                updateSort={this.updateSort}
            />
        );

        const containerStyles = fullPage
            ? classNames(styles.container, styles.fullpage)
            : modal
            ? classNames(styles.container, styles.modal)
            : styles.container;

        const shouldRender = Boolean(ratings.allowPublicReviews);

        const withWrittenReviews =
            ratings?.reviews?.length > 0
                ? ratings.reviews.filter((review) => {
                      return review.review.length > 0;
                  })
                : [{ review: '' }];

        let firstReview = '';
        if (Boolean(withWrittenReviews[0]?.review)) {
            firstReview = withWrittenReviews[0]?.review;
        }
        return (
            shouldRender && (
                <>
                    {fullPage && (
                        <div>
                            <RatingsPageHeaderMeta
                                baseUrl={baseUrl}
                                canonicalURL={canonicalURL}
                                firstReview={firstReview}
                                ratings={ratings}
                                seller={seller}
                            />
                            {!whiteLabel && (
                                <RatingsPageBreadcrumbs sellerId={seller.sellerId} sellerName={seller.name} />
                            )}
                        </div>
                    )}
                    <div className={containerStyles}>
                        {fullPage}
                        {header}
                        <RatingsSummary
                            fullPage={fullPage}
                            mobile={mobile}
                            modal={modal}
                            percentiles={percentiles}
                            ratings={ratings}
                            seller={seller}
                            updateFilterFromBarChart={this.updateFilterFromBarChart}
                        />
                        {reviews}
                        {!fullPage && (
                            <div className={styles.buttonContainer}>
                                <ButtonLink
                                    className={styles.reviewsPageLinkButton}
                                    onClick={dismissReviewsModal}
                                    url={fullPageLink}
                                >
                                    {ratings?.totalReviews !== 1 ? (
                                        <FormattedMessage
                                            id="review.page_link_button"
                                            values={{ count: totalReviews }}
                                        />
                                    ) : (
                                        <FormattedMessage id="review.page_link_button_singular" />
                                    )}
                                </ButtonLink>
                            </div>
                        )}
                    </div>
                </>
            )
        );
    }
}
